import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApolloError, useMutation } from '@apollo/client'
import {
  DELETE_MOBILE_USER_ACCOUNT,
  IDeleteMobileUserAccountVars,
  IDeleteMobileUserAccountData,
} from '../../graphql/hid/deleteMobileUserAccount'
import Error from '../Shared/Error'
import { useSnackbar } from '../Shared/useSnackbar'
import { IOrigoCard } from '../../graphql/hid/getOrigoAccount'
import { useForm, Controller } from 'react-hook-form'
import { Box, Typography, TextField, Dialog } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { colors } from '../../const/colors'

type FormData = {
  comment: string
}

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: colors.surface,
    fontSize: '14px',
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.slate },
    '&.Mui-focused fieldset': { borderColor: colors.navy, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    color: colors.slate,
    '&.Mui-focused': { color: colors.navy },
  },
} as const

const DeleteHIDCard = ({
  card,
  refetch,
  setDeletionProcess,
}: {
  card: IOrigoCard
  refetch: () => void
  setDeletionProcess: (v: boolean) => void
}) => {
  const { t } = useTranslation(['hid', 'btn'])
  const { notify } = useSnackbar()
  const [openModal, setOpenModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [expanded, setExpanded] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>()

  const { user, deviceId, cardNumber } = card

  const onCompleted = (data: IDeleteMobileUserAccountData) => {
    const { status, error } = data.DeleteMobileUserAccount
    if (status) {
      refetch()
      notify(t('card_deleted'))
      setDeletionProcess(true)
      setExpanded(false)
    } else error && setErrorMessage(`${error}`)
  }

  const [deleteHIDCard] = useMutation<
    IDeleteMobileUserAccountData,
    IDeleteMobileUserAccountVars
  >(DELETE_MOBILE_USER_ACCOUNT, {
    onCompleted,
    onError: (error: ApolloError) => setErrorMessage(error.message),
  })

  const deleteCard = async (data: FormData) => {
    const { comment } = data
    await deleteHIDCard({ variables: { login: user.login, deviceId, comment } })
    setOpenModal(false)
  }

  if (!deviceId) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          mt: '8px',
          p: '14px 16px',
          borderRadius: '14px',
          background: '#FFF7ED',
          border: '1px solid #FED7AA',
        }}
      >
        <PhoneAndroidOutlinedIcon
          sx={{ fontSize: 20, color: '#EA580C', flexShrink: 0, mt: '1px' }}
        />
        <Box>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 700, color: '#9A3412', mb: '4px' }}
          >
            {t('awaiting_installation')}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#9A3412', lineHeight: 1.55 }}>
            {t('no_device_warning')}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {!expanded ? (
        <Box
          component="button"
          onClick={() => setExpanded(true)}
          data-e2e="delete-mobile-card"
          sx={{
            width: '100%',
            height: 40,
            borderRadius: '12px',
            border: '1px solid rgba(192,57,43,0.2)',
            background: 'transparent',
            color: '#dc2626',
            fontWeight: 700,
            fontSize: '13.5px',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            cursor: 'pointer',
            transition: 'background .15s, border-color .15s',
            '&:hover': { background: '#FEF2F2', borderColor: '#f87171' },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          {t('delete_card')}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            p: '14px',
            borderRadius: '14px',
            border: `1px solid ${colors.border}`,
            background: colors.surface,
          }}
        >
          <Controller
            name="comment"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label={t('deletion_comment')}
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                error={!!errors.comment}
                helperText={errors.comment?.message}
                sx={FIELD_SX}
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Box
              component="button"
              onClick={() => setExpanded(false)}
              sx={{
                flex: 1,
                height: 40,
                borderRadius: '10px',
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.slate,
                fontWeight: 600,
                fontSize: '13.5px',
                fontFamily: 'inherit',
                cursor: 'pointer',
                transition: 'background .15s',
                '&:hover': { background: colors.bg },
              }}
            >
              {t('btn:cancel')}
            </Box>
            <Box
              component="button"
              onClick={() => setOpenModal(true)}
              data-e2e="delete-mobile-card-submit"
              sx={{
                flex: 2,
                height: 40,
                borderRadius: '10px',
                border: `1px solid ${colors.redBorder}`,
                background: 'transparent',
                color: '#dc2626',
                fontWeight: 700,
                fontSize: '13.5px',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'background .15s, border-color .15s',
                '&:hover': { background: '#FEF2F2', borderColor: '#f87171' },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              {t('delete_card')}
            </Box>
          </Box>
        </Box>
      )}

      {errorMessage && <Error errorMessage={errorMessage} />}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: '8px',
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 24px 60px -20px rgba(14,44,59,.35)',
          },
        }}
      >
        <Box sx={{ p: '20px 20px 8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                background: colors.redBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 22, color: colors.red }} />
            </Box>
            <Typography sx={{ fontSize: '17px', fontWeight: 800, color: colors.navy }}>
              {t('delete_access_card')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '15.5px', color: colors.textBody, lineHeight: 1.65 }}>
            {t('delete_card_confirmation')}
            {' '}
            <Box component="span" sx={{ fontWeight: 700, color: colors.navy, fontFamily: 'monospace' }}>
              {cardNumber}
            </Box>
            ?
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', p: '16px 20px 12px' }}>
          <Box
            component="button"
            onClick={() => setOpenModal(false)}
            sx={{
              flex: 1,
              height: 42,
              borderRadius: '11px',
              border: `1px solid ${colors.border}`,
              background: colors.bg,
              color: colors.slate,
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: 'background .15s',
              '&:hover': { background: colors.border },
            }}
          >
            {t('btn:cancel')}
          </Box>
          <Box
            component="button"
            onClick={handleSubmit(deleteCard)}
            data-e2e="delete-mobile-card-confirm"
            sx={{
              flex: 2,
              height: 42,
              borderRadius: '11px',
              border: 'none',
              background: colors.red,
              color: '#fff',
              fontWeight: 700,
              fontSize: '14px',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: colors.shadowBtnRed,
              transition: 'background .15s',
              '&:hover': { background: colors.redDark },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            {t('delete_card')}
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

export default DeleteHIDCard
